import React from "react";
import { Box, Typography, Card, CardContent, Divider, Paper } from "@mui/material";

const AlgorithmDocumentation: React.FC = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        📚 Estimate Decline Optimization Algorithm
      </Typography>

      {/* Executive Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Executive Summary
          </Typography>
          <Typography variant="body1" paragraph>
            This algorithm addresses the core business problem: when customers decline repair estimates, 
            we typically only collect a small trip fee (~$129, ~$40 profit) instead of the full repair 
            margin (~$300 profit on a $500 repair). The objective is to model and simulate pricing changes 
            and counter-offers to improve acceptance rates and overall profitability.
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "#fff3cd", border: "1px solid #ffc107" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ Key Insight
            </Typography>
            <Typography variant="body2">
              Blanket price reductions can backfire. Reducing prices from $500 to $400 across all customers 
              can decrease profit from $170K to $152K despite a 40% increase in acceptance. However, targeted 
              price reductions only to customers who decline can increase profit to $202K.
            </Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Core Algorithm */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            1. Base Economics Model
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: "1.1rem" }}>
            Per Service Order Variables
          </Typography>
          
          <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mb: 2, fontFamily: "monospace" }}>
            <Typography variant="body2" sx={{ mb: 1 }}><strong>Input Variables:</strong></Typography>
            <Typography variant="body2">• job_code: The specific repair job code</Typography>
            <Typography variant="body2">• initial_estimate: The quoted price (e.g., $500)</Typography>
            <Typography variant="body2">• labor_cost: Direct labor cost component</Typography>
            <Typography variant="body2">• parts_cost: Direct parts cost component</Typography>
            <Typography variant="body2">• total_cost: Total internal cost (~$200 in examples)</Typography>
            <Typography variant="body2">• trip_fee: Diagnosis/trip charge ($129 standard)</Typography>
            <Typography variant="body2">• trip_fee_cost: Cost to deliver trip ($89 in examples)</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>• customer_accepts: Boolean (Yes/No decision)</Typography>
            
            <Typography variant="body2" sx={{ mb: 1 }}><strong>Derived Variables:</strong></Typography>
            <Typography variant="body2">• repair_profit = initial_estimate - total_cost</Typography>
            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>Example: $500 - $200 = $300</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>• trip_fee_profit = trip_fee - trip_fee_cost</Typography>
            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>Example: $129 - $89 = $40</Typography>
          </Paper>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: "1.1rem" }}>
            Outcome Logic
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "#f5f5f5", fontFamily: "monospace" }}>
            <Typography variant="body2">IF customer_accepts == YES:</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>revenue = initial_estimate</Typography>
            <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>profit = repair_profit</Typography>
            <Typography variant="body2">ELSE:</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>revenue = trip_fee</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>profit = trip_fee_profit</Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Scenario Simulation Logic */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            2. Scenario Simulation Logic
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: "1.1rem" }}>
            Baseline Scenario (Current State)
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mb: 2, fontFamily: "monospace", fontSize: "0.875rem" }}>
            <Typography variant="body2">accepts = total_orders × current_acceptance_rate</Typography>
            <Typography variant="body2">declines = total_orders × (1 - current_acceptance_rate)</Typography>
            <Typography variant="body2">total_profit = (accepts × repair_profit) + (declines × trip_fee_profit)</Typography>
          </Paper>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: "1.1rem" }}>
            Blanket Price Change Scenario
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mb: 2, fontFamily: "monospace", fontSize: "0.875rem" }}>
            <Typography variant="body2">new_acceptance_rate = old_rate × (1 + (price_change_% × elasticity))</Typography>
            <Typography variant="body2">accepts = total_orders × new_acceptance_rate</Typography>
            <Typography variant="body2">declines = total_orders × (1 - new_acceptance_rate)</Typography>
            <Typography variant="body2">new_repair_profit = repair_profit × discount_factor</Typography>
            <Typography variant="body2">total_profit = (accepts × new_repair_profit) + (declines × trip_fee_profit)</Typography>
          </Paper>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: "1.1rem" }}>
            Targeted Offers (Only for Declines)
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mb: 2, fontFamily: "monospace", fontSize: "0.875rem" }}>
            <Typography variant="body2">initial_accepts = total_orders × current_acceptance_rate</Typography>
            <Typography variant="body2">initial_declines = total_orders × (1 - current_acceptance_rate)</Typography>
            <Typography variant="body2">converted_accepts = initial_declines × conversion_rate</Typography>
            <Typography variant="body2">remaining_declines = initial_declines - converted_accepts</Typography>
            <Typography variant="body2">total_profit = (initial_accepts × full_profit) +</Typography>
            <Typography variant="body2" sx={{ ml: 13 }}>(converted_accepts × discounted_profit) +</Typography>
            <Typography variant="body2" sx={{ ml: 13 }}>(remaining_declines × trip_fee_profit)</Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Example Calculations */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            3. Example Calculations
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: "1.1rem" }}>
            Scenario 1: 50% Acceptance Rate
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Baseline (Price = $500):
            </Typography>
            <Paper sx={{ p: 2, bgcolor: "#e8f5e9" }}>
              <Typography variant="body2">• 1000 orders</Typography>
              <Typography variant="body2">• 500 accept → 500 × $300 = $150,000</Typography>
              <Typography variant="body2">• 500 decline → 500 × $40 = $20,000</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                Total profit = $170,000
              </Typography>
            </Paper>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Blanket reduction to $400:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: "#ffebee" }}>
              <Typography variant="body2">• 700 accept → 700 × $200 = $140,000</Typography>
              <Typography variant="body2">• 300 decline → 300 × $40 = $12,000</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, color: "error.main" }}>
                Total profit = $152,000 (decrease of $18K or 10.6%)
              </Typography>
            </Paper>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Targeted reduction (only for declines):
            </Typography>
            <Paper sx={{ p: 2, bgcolor: "#e3f2fd" }}>
              <Typography variant="body2">• 500 accept at $500 → 500 × $300 = $150,000</Typography>
              <Typography variant="body2">• 200 of 500 declines convert at $400 → 200 × $200 = $40,000</Typography>
              <Typography variant="body2">• 300 still decline → 300 × $40 = $12,000</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, color: "success.main" }}>
                Total profit = $202,000 (increase of $32K or 18.8%)
              </Typography>
            </Paper>
          </Box>
        </CardContent>
      </Card>

      {/* Key Business Rules */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            4. Key Business Rules
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              1. Price Elasticity Matters
            </Typography>
            <Typography variant="body2">
              The relationship between price changes and acceptance rate changes is critical. A 20% price 
              reduction ($500→$400) generating a 40% increase in acceptance (50%→70%) represents significant 
              price elasticity of demand.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              2. Acceptance Rate Dependency
            </Typography>
            <Typography variant="body2">
              • High acceptance rates (50%+) make blanket discounts unprofitable
            </Typography>
            <Typography variant="body2">
              • Low acceptance rates (10-20%) make blanket discounts potentially profitable
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              3. Targeted Offers Superior
            </Typography>
            <Typography variant="body2">
              Offering reduced prices only to customers who decline preserves higher margins on natural 
              acceptances while capturing additional conversions.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              4. Repair vs Replace Analysis
            </Typography>
            <Typography variant="body2">
              When repair cost exceeds 80% of replacement cost, customers are significantly more likely to 
              decline. This ratio is a key indicator for pricing strategy and counter-offer effectiveness.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Contextual Variables */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            5. Extended Variables for Decision-Making
          </Typography>

          <Typography variant="body1" paragraph>
            The algorithm incorporates these contextual factors to improve prediction accuracy:
          </Typography>

          <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Contextual Variables:</Typography>
            <Typography variant="body2">• appliance_age: Years since purchase</Typography>
            <Typography variant="body2">• appliance_condition: Assessment of reliability</Typography>
            <Typography variant="body2">• repair_vs_replacement_cost_ratio: repair_cost / new_appliance_cost</Typography>
            <Typography variant="body2">• customer_home_value: Proxy for affordability</Typography>
            <Typography variant="body2">• expected_repair_longevity: Estimated years before next failure</Typography>
            <Typography variant="body2">• replacement_financing_available: Boolean (e.g., 0% financing)</Typography>
            <Typography variant="body2">• appliance_type: (refrigerator, microwave, HVAC, etc.)</Typography>
            <Typography variant="body2">• job_type_complexity: Difficulty/reliability of repair</Typography>
          </Paper>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: "1.1rem" }}>
            Decision Factors
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "#f5f5f5", fontFamily: "monospace", fontSize: "0.875rem" }}>
            <Typography variant="body2" sx={{ mb: 1 }}><strong>Affordability Analysis:</strong></Typography>
            <Typography variant="body2">IF home_value &gt; threshold_high:</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>price_sensitivity = LOW</Typography>
            <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>discount_effectiveness = LOW</Typography>
            
            <Typography variant="body2" sx={{ mb: 1, mt: 2 }}><strong>Repair Viability:</strong></Typography>
            <Typography variant="body2">IF (appliance_age &gt; useful_life) AND (repair_cost &gt; 0.5 × replacement_cost):</Typography>
            <Typography variant="body2" sx={{ ml: 2, mb: 1 }}>recommend_replacement = TRUE</Typography>
            
            <Typography variant="body2" sx={{ mb: 1, mt: 2 }}><strong>Price Point Testing:</strong></Typography>
            <Typography variant="body2">FOR EACH price_point IN [90%, 85%, 80%, 75% of current]:</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>estimate_acceptance_probability(price_point, contextual_vars)</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>calculate_expected_profit()</Typography>
            <Typography variant="body2">optimal_price = MAX(expected_profit)</Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Implementation Notes */}
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            6. Implementation Notes
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Data Requirements
            </Typography>
            <Typography variant="body2">
              • Top 200 D2C job codes by volume
            </Typography>
            <Typography variant="body2">
              • 12 months of historical service order data
            </Typography>
            <Typography variant="body2">
              • Estimate prices, acceptance/decline data, profit margins
            </Typography>
            <Typography variant="body2">
              • Appliance details and recommended replacement models
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Price Elasticity
            </Typography>
            <Typography variant="body2">
              Default elasticity is set to -1.5, meaning a 1% price decrease leads to a 1.5% increase in 
              acceptance rate. This can be adjusted based on job code characteristics and historical data.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Success Metrics
            </Typography>
            <Typography variant="body2">
              • Target: +5-7 percentage points in acceptance rate
            </Typography>
            <Typography variant="body2">
              • Target: +10-20% profit per service order
            </Typography>
            <Typography variant="body2">
              • Expected: $2-4M annual profit improvement
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AlgorithmDocumentation;
